/**
 * Cube座標系でのヘクサゴン座標
 * 制約: x + y + z = 0
 */
export interface HexCoord {
  x: number;
  y: number;
  z: number;
}

/**
 * ヘクサゴンの6方向
 */
export enum HexDirection {
  RIGHT = 0,       // (+1, -1,  0)
  TOP_RIGHT = 1,   // (+1,  0, -1)
  TOP_LEFT = 2,    // ( 0, +1, -1)
  LEFT = 3,        // (-1, +1,  0)
  BOTTOM_LEFT = 4, // (-1,  0, +1)
  BOTTOM_RIGHT = 5 // ( 0, -1, +1)
}

/**
 * 各方向への移動ベクトル
 */
export const HEX_DIRECTIONS: readonly HexCoord[] = Object.freeze([
  { x: +1, y: -1, z:  0 }, // RIGHT
  { x: +1, y:  0, z: -1 }, // TOP_RIGHT
  { x:  0, y: +1, z: -1 }, // TOP_LEFT
  { x: -1, y: +1, z:  0 }, // LEFT
  { x: -1, y:  0, z: +1 }, // BOTTOM_LEFT
  { x:  0, y: -1, z: +1 }  // BOTTOM_RIGHT
]);

/**
 * タイルの種類
 */
export enum TileType {
  BLUE_1 = 'BLUE_1',           // 青丸1
  RED_1 = 'RED_1',             // 赤丸1
  BLUE_2 = 'BLUE_2',           // 青丸2
  RED_2 = 'RED_2',             // 赤丸2
  BLUE_1_RED_1 = 'BLUE_1_RED_1', // 青丸1 赤丸1
  BLUE_2_RED_1 = 'BLUE_2_RED_1', // 青丸2 赤丸1
  BLUE_1_RED_2 = 'BLUE_1_RED_2', // 青丸1 赤丸2
  // 星マーク付きタイル
  BLUE_1_STAR = 'BLUE_1_STAR',           // 青丸1★
  RED_1_STAR = 'RED_1_STAR',             // 赤丸1★
  BLUE_2_STAR = 'BLUE_2_STAR',           // 青丸2★
  RED_2_STAR = 'RED_2_STAR',             // 赤丸2★
  BLUE_1_RED_1_STAR = 'BLUE_1_RED_1_STAR', // 青丸1 赤丸1★
  BLUE_2_RED_1_STAR = 'BLUE_2_RED_1_STAR', // 青丸2 赤丸1★
  BLUE_1_RED_2_STAR = 'BLUE_1_RED_2_STAR', // 青丸1 赤丸2★
  EMPTY = 'EMPTY'              // 空のタイル（19タイルに満たない場合）
}

/**
 * タイルの状態
 */
export interface HexTileData {
  id: string;
  coords: HexCoord;
  isHighlighted: boolean;
  isSelected: boolean;
  ring: number; // 0: 中央, 1: 第1リング, 2: 第2リング
  tileType: TileType;
}

/**
 * タイル配置設定
 */
export interface TileConfig {
  [TileType.BLUE_1]: number;
  [TileType.RED_1]: number;
  [TileType.BLUE_2]: number;
  [TileType.RED_2]: number;
  [TileType.BLUE_1_RED_1]: number;
  [TileType.BLUE_2_RED_1]: number;
  [TileType.BLUE_1_RED_2]: number;
  // 星マーク付き
  [TileType.BLUE_1_STAR]: number;
  [TileType.RED_1_STAR]: number;
  [TileType.BLUE_2_STAR]: number;
  [TileType.RED_2_STAR]: number;
  [TileType.BLUE_1_RED_1_STAR]: number;
  [TileType.BLUE_2_RED_1_STAR]: number;
  [TileType.BLUE_1_RED_2_STAR]: number;
  [TileType.EMPTY]?: number;  // オプショナルとして追加
}

/**
 * デフォルトのタイル配置設定
 */
export const DEFAULT_TILE_CONFIG: TileConfig = {
  [TileType.BLUE_1]: 3,
  [TileType.RED_1]: 3,
  [TileType.BLUE_2]: 2,
  [TileType.RED_2]: 2,
  [TileType.BLUE_1_RED_1]: 3,
  [TileType.BLUE_2_RED_1]: 2,
  [TileType.BLUE_1_RED_2]: 1,
  // 星マーク付き
  [TileType.BLUE_1_STAR]: 2,
  [TileType.RED_1_STAR]: 2,
  [TileType.BLUE_2_STAR]: 1,
  [TileType.RED_2_STAR]: 1,
  [TileType.BLUE_1_RED_1_STAR]: 2,
  [TileType.BLUE_2_RED_1_STAR]: 1,
  [TileType.BLUE_1_RED_2_STAR]: 1
};

/**
 * ピクセル座標
 */
export interface PixelCoord {
  x: number;
  y: number;
}

/**
 * ヘクサゴンのレイアウト設定
 */
export interface HexLayout {
  size: number;      // タイルの半径
  spacing: number;   // タイル間のスペース
  origin: PixelCoord; // Canvas上の原点
}